<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !resourceLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12">
          <label>{{ $t('description') }}</label>
          <p class="label-info">
            {{ $t('description-resource-message') }}
          </p>
          <tiptap-vuetify
            v-model="resource.description"
            :placeholder="'Max length is 2000 characters'"
            :extensions="extensions"
            class="required"
            :disabled="!edit"
            :rules="requiredRule()"
          ></tiptap-vuetify>
          <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
            {{ charCount }} / {{ maxChars }} characters
          </div>
        </v-col>
        <v-col cols="12">
          <label>{{ $t('tagline') }}</label>
          <p class="label-info">
            {{ $t('tagline-info') }}
          </p>
          <v-text-field
            v-model="resource.tagline"
            outlined
            :rules="minMaxLengthRule(1, 100)"
            class="required"
            :disabled="!edit"
            data-cy="description"
            :placeholder="'TagLine'"
            v-on:keydown.enter.prevent
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <label>Use Cases</label>
              <p class="label-info">{{ $t('user-cases-info') }}</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                depressed
                rounded
                large
                color="primary200"
                class="white--text font-weight-bold"
                @click="addUseCase()"
                :disabled="!edit"
              >
                <v-icon left>
                  $vuetify.icons.plus
                </v-icon>
                <span class="text-decoration-underline text-transform-none font-weight-bold">
                  {{ $t('add-use-case') }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row v-for="(useCase, i) in resource.useCases" :key="'use-case-' + i">
            <v-col cols="10">
              <v-text-field
                v-model="useCase.text"
                :placeholder="'Enter a use case'"
                outlined
                :rules="minMaxLengthRule(1, 500)"
                class="required"
                data-cy="usecase"
                :disabled="!edit"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="2">
              <v-btn :disabled="!edit" fab small text @click="removeUseCase(i)">
                <v-icon>
                  $vuetify.icons.trash
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('logo') }}</label>
                    <p class="label-info">
                      {{ $t('logo-resource-info') }}
                    </p>
                    <v-text-field
                      v-model="resource.logoUrl"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="logoUrl"
                      :placeholder="$t('paste-url')"
                      :rules="checkURL()"
                      prepend-inner-icon="mdi-link-variant"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('picture') }}</label>
                    <p class="label-info">{{ $t('picture-resource-info') }}<br /><br /></p>
                    <v-text-field
                      v-model="resource.picture"
                      outlined
                      clearable
                      :disabled="!edit"
                      :placeholder="$t('paste-url')"
                      :rules="checkURL()"
                      prepend-inner-icon="mdi-link-variant"
                    ></v-text-field>
                  </v-col>

                  <v-col v-if="false" cols="12">
                    <v-row>
                      <v-col cols="6">
                        <label>{{ $t('multimedia') }}</label>
                        <p class="label-info">{{ $t('multimedia-message') }}</p>
                      </v-col>
                      <v-col cols="6">
                        <label>{{ $t('multimedia-name') }}</label>
                        <p class="label-info">{{ $t('multimedia-name-message') }}</p>
                      </v-col>
                    </v-row>
                    <v-row v-for="(multimedia, i) in resource.multimedias" :key="i">
                      <v-col cols="6">
                        <v-text-field
                          v-model="multimedia.url"
                          :placeholder="'Paste a Url'"
                          outlined
                          :disabled="!edit"
                          data-cy="multimedia-url"
                          prepend-inner-icon="mdi-link-variant"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="multimedia.name"
                          :placeholder="$t('multimedia-name')"
                          outlined
                          :disabled="!edit"
                          data-cy="multimedia-url"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row v-if="edit">
                      <v-col cols="12">
                        <v-btn tile text @click="addMultimediaLink()">
                          <v-icon left>
                            $vuetify.icons.plus
                          </v-icon>
                          <span class="text-decoration-underline text-small text-bold">
                            {{ $t('add-multimedia-link') }}
                          </span>
                        </v-btn>
                      </v-col>
                      <v-col cols="6" class="pt-0">
                        <v-btn if="edit" fab tile text @click="removeMultimediaLink()">
                          <v-icon left>
                            $vuetify.icons.trash
                          </v-icon>
                          <span id="remove" class="text-decoration-underline text-small">
                            {{ $t('remove-multimedia-link') }}
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ 'Related Services' }}</label>
                    <p class="label-info">
                      {{ 'Select any related services' }}
                    </p>
                    <input-autocomplete-search
                      v-model="resource.relatedResources"
                      multiple
                      chips
                      label="Related Services"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      return-object
                      outlined
                      :item-value="'id'"
                      :item-text="'name'"
                      :disabled="!edit"
                      :url="'/api/v1/resources/by-name?name='"
                    ></input-autocomplete-search>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="my-5" v-if="next" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="back()">
        <v-icon color="black">$vuetify.icons.back</v-icon>
        {{ $t('back') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || resource.useCases.length == 0 || charCount > maxChars"
        >{{ $t('next') }}
        <v-icon color="white">$vuetify.icons.next</v-icon>
      </v-btn>
    </v-row>
    <v-row class="my-5" v-if="!next && edit" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="cancel()">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || resource.useCases.length == 0 || charCount > maxChars"
        >{{ $t('update') }}
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'ResourceMarketingInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: null,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    InputAutocompleteSearch,
    AlertUnsavedChanges,
    TiptapVuetify,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      resource: {
        id: null,
        description: null,
        logoUrl: null,
        picture: null,
        tagline: null,
        multimedias: [],
        useCases: [],
        relatedResources: [],
      },
      resourceLoading: true,
      extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
      maxChars: 2000,
    };
  },
  mounted() {
    this.loadingComponents++;
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
      this.unsavedChanges = false;
    },
    back() {
      console.log('back!');
      this.$emit('tab', 0);
    },
    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    },
    add() {
      if (!this.resource.description || this.stripHtml(this.resource.description).trim() === '') {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Description Required'),
          color: 'error',
        });
        return;
      }

      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/resources/marketing-info', this.resource)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resource-updated'),
          });
          this.unsavedChanges = false;
          this.$emit('tab', 2);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    removeMultimediaLink() {
      this.resource.multimedias.pop();
    },
    addMultimediaLink() {
      this.resource.multimedias.push({ url: null, name: null });
    },
    addUseCase() {
      this.resource.useCases.push({ text: null });
    },
    removeUseCase(index) {
      this.resource.useCases.splice(index, 1);
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.resource.description || '';
      return div.textContent.length;
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        console.log('Loading');
        if (this.id != null) {
          this.axios
            .get('/api/v1/resources/' + this.id)
            .then(res => {
              this.resource = res.data;
              this.$nextTick(() => {
                this.resourceLoading = false;
              });
            })
            .catch(error => {
              console.log({ error });
            });
        } else {
          this.resourceLoading = false;
        }
      }
    },
    resource: {
      handler: function(val, oldVal) {
        console.log('[Resource] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
